import { useEffect } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  List,
  Popover,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  FileSearchOutlined,
  OrderedListOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { taskStatusColor, taskStatusLabel } from '../utils/taskStatusDisplay';
import { RulesPanel, type RuleItem } from '../components/RulesPanel';
import { useIsMobile } from '../hooks/useMobile';

/**
 * 直属领导侧的「岗位胜任力 + 高潜识别」列表页（V1.2 合并版）。
 *
 * V1.2.1（2026-04-30 22:45 起）：
 *   - 打分体验改为路由跳转（/leader/competency/:id），与「素质项评价」一致
 *   - 本页面仅负责：列表 + 统计 + 规则面板 + 入口点击
 *   - 实际表单逻辑见 LeaderCompFormPage.tsx（独立页面，PC 与手机端统一走路由）
 *
 * 这是用户在 2026-04-30 22:45 反馈"网页版希望像素质项评价一样跳到独立页面"
 * 之后做的整改 —— 之前胜任力是 Modal 弹窗，PC / 手机各有体验差异；
 * 现在统一为独立路由，URL 可分享、浏览器后退按钮可用。
 */

interface HpTaskListItem {
  evaluatee: { id: number };
  assessment?: { isHighPotential?: boolean; status?: string } | null;
}

const COMPETENCY_RULES: RuleItem[] = [
  {
    title: '对照锚定逐档判断，不允许跳级',
    desc: '每个维度 5 档锚定描述代表能力的进阶。请逐档对照下属的实际工作表现，选择最贴近的档位。遵循"低档全部满足才能进入下一档"的原则——不要为了体面跳级给高分，也不要因为某次失误一刀切打低分。',
    color: '#722ed1',
    icon: <OrderedListOutlined />,
  },
  {
    title: '基于事实，全周期综合',
    desc: '评价依据是下属在本评价周期内的整体工作行为，而非单次事件或近期印象（避免"近因效应"）。重点参考你亲自观察到的关键工作场景，如项目交付、问题处理、跨部门协作等。如对某维度接触不足，请在评语中说明信息缺口，避免凭印象给分。',
    color: '#1677ff',
    icon: <FileSearchOutlined />,
  },
  {
    title: '红线判定，独立谨慎',
    desc: '三条红线为"一票否决项"，任一被勾选"已触发"将取消该下属本周期的晋升评优资格。红线与维度打分是两套独立机制——请基于客观事实独立判断；勾选"已触发"时务必附"具体事件 + 时间"备注，便于 HR 复核；未触发请保持"未触发"，不要为求"看起来公平"全部勾上。',
    color: '#ff4d4f',
    icon: <WarningOutlined />,
  },
  {
    title: '胜任力评完直接判高潜（V1.2）',
    desc: '在打分页尾部「高潜识别」一栏勾选「是 / 否」。是 → 填四维评分（成长性 / 雄心抱负 / 学习意愿 / 价值观契合，每档有锚定）+ STAR 案例 + 培养方向；否 → 不必填四维 / STAR / 培养方向，直接提交。两件事一并完成，不需要再去额外页面。',
    color: '#fa8c16',
    icon: <CrownOutlined />,
  },
];

function CompetencyEvaluateePopoverContent({ row }: { row: any }) {
  const e = row.evaluatee ?? {};
  return (
    <div style={{ maxWidth: 300, whiteSpace: 'normal', lineHeight: 1.65, fontSize: 13 }}>
      <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 8 }}>岗位胜任力 + 高潜识别</div>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>{e.name ?? '—'}</div>
      <div>部门：{e.deptPath ?? '—'}</div>
      <div>职位：{e.position ?? '—'}</div>
      <div style={{ marginTop: 10, fontSize: 12, color: '#595959' }}>
        当前角色：<Tag style={{ margin: 0 }} color="blue">直属上级评价</Tag>
      </div>
    </div>
  );
}

export function LeaderCompetencyPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  /**
   * 老深链入口兼容：/leader/competency?openEvaluatee=123 直接跳到打分页。
   * （V1.2 起改为独立路由 /leader/competency/:id；保留旧 query 形式让"我的评价"
   * 等历史链接还能用。）
   */
  useEffect(() => {
    const raw = searchParams.get('openEvaluatee');
    if (!raw) return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return;
    setSearchParams({}, { replace: true });
    navigate(`/leader/competency/${n}`);
  }, [searchParams, setSearchParams, navigate]);

  const { data: tasks = [] } = useQuery({
    queryKey: ['my-competency-tasks'],
    queryFn: () => api.get('/leader/competency-tasks'),
  });

  /** 列表页一次性拉「我的高潜任务列表」，给每行展示「高潜状态」标签。 */
  const { data: hpTasks = [] } = useQuery<HpTaskListItem[]>({
    queryKey: ['my-hp-tasks-overview'],
    queryFn: () => api.get('/leader/hp-tasks'),
  });

  const taskList = tasks as any[];
  const totalTasks = taskList.length;
  const doneTasks = taskList.filter((t) => t.status === 'SUBMITTED').length;
  const pendingTasks = totalTasks - doneTasks;
  const percentTasks = totalTasks === 0 ? 0 : Math.round((doneTasks / totalTasks) * 100);

  const hpStatusByEmpId = new Map<number, { isHp?: boolean; status?: string }>();
  for (const t of hpTasks) {
    hpStatusByEmpId.set(t.evaluatee.id, {
      isHp: t.assessment?.isHighPotential,
      status: t.assessment?.status,
    });
  }

  const goEval = (evaluateeId: number) => {
    navigate(`/leader/competency/${evaluateeId}`);
  };

  return (
    <div>
      <div className="tg-page-title" style={{ marginBottom: 8 }}>
        岗位胜任力评价 + 高潜识别
      </div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        直属领导对每位下属在同一份表单内完成两件事：① 岗位胜任力维度打分 + 红线判定；② 是否为高潜人才（是则填四维 + STAR + 培养方向）。点击下属即可跳转至独立打分页面。
      </Typography.Paragraph>

      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
        <Row gutter={16} align="middle">
          <Col xs={24} md={16}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title={
                    <span>
                      <TeamOutlined style={{ marginRight: 6 }} />
                      下属共
                    </span>
                  }
                  value={totalTasks}
                  suffix="人"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={
                    <span>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                      已完成
                    </span>
                  }
                  value={doneTasks}
                  valueStyle={{ color: '#52c41a' }}
                  suffix="人"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={
                    <span>
                      <ClockCircleOutlined
                        style={{ color: pendingTasks ? '#fa8c16' : '#999', marginRight: 6 }}
                      />
                      待完成
                    </span>
                  }
                  value={pendingTasks}
                  valueStyle={{ color: pendingTasks ? '#fa8c16' : '#999' }}
                  suffix="人"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ paddingLeft: 12 }}>
              <div style={{ marginBottom: 4, color: '#666', fontSize: 13 }}>完成进度</div>
              <Progress
                percent={percentTasks}
                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                format={(p) => `${p}%`}
              />
            </div>
          </Col>
        </Row>
      </Card>

      <RulesPanel rules={COMPETENCY_RULES} />

      <Card styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        {isMobile ? (
          <List
            dataSource={taskList}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="暂无下属胜任力 / 高潜评估任务"
                />
              ),
            }}
            split={false}
            pagination={taskList.length > 8 ? { pageSize: 8, showTotal: (n) => `共 ${n} 项` } : false}
            renderItem={(r: any) => {
              const submitted = r.status === 'SUBMITTED';
              const dept = r.evaluatee?.deptPath?.split('/').slice(-2).join(' / ') ?? '—';
              const position = r.evaluatee?.position ?? '—';
              const hpInfo = hpStatusByEmpId.get(r.evaluateeId);
              const hpTag = !hpInfo?.status ? (
                <Tag color={taskStatusColor('PENDING')}>
                  高潜：{taskStatusLabel('PENDING')}
                </Tag>
              ) : hpInfo.status === 'SUBMITTED' ? (
                hpInfo.isHp ? (
                  <Tag color="gold" icon={<CrownOutlined />} style={{ marginRight: 0 }}>
                    高潜：已识别
                  </Tag>
                ) : (
                  <Tag color="blue" style={{ marginRight: 0 }}>
                    高潜：非高潜
                  </Tag>
                )
              ) : (
                <Tag color={taskStatusColor(hpInfo.status)} style={{ marginRight: 0 }}>
                  高潜：{taskStatusLabel(hpInfo.status)}
                </Tag>
              );
              return (
                <List.Item style={{ padding: '6px 0', borderBottom: 'none' }}>
                  <Card
                    size="small"
                    style={{
                      width: '100%',
                      borderRadius: 12,
                      border: '1px solid rgba(124,92,191,0.15)',
                      boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
                    }}
                    styles={{ body: { padding: 14 } }}
                    onClick={() => goEval(r.id)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 8,
                        gap: 8,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: '#1f1f1f',
                            lineHeight: 1.3,
                          }}
                        >
                          {r.evaluatee?.name ?? '—'}
                        </div>
                        <div
                          style={{
                            color: '#94a3b8',
                            fontSize: 12,
                            marginTop: 4,
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                          }}
                        >
                          {dept} · {position}
                        </div>
                      </div>
                      <Tag color={taskStatusColor(r.status)} style={{ marginRight: 0, flexShrink: 0 }}>
                        胜任力：{taskStatusLabel(r.status)}
                      </Tag>
                    </div>
                    <div style={{ marginBottom: 12 }}>{hpTag}</div>
                    <Button
                      block
                      type={submitted ? 'default' : 'primary'}
                      size="large"
                      onClick={(e) => {
                        e.stopPropagation();
                        goEval(r.id);
                      }}
                    >
                      {submitted ? '查看 / 修改' : '去打分'}
                    </Button>
                  </Card>
                </List.Item>
              );
            }}
          />
        ) : (
          <Table
            rowKey={(r: any) => `${r.batchId}-${r.evaluateeId}`}
            dataSource={taskList}
            pagination={{ pageSize: 10, showTotal: (n) => `共 ${n} 项` }}
            columns={[
              {
                title: '下属',
                width: 110,
                render: (_: unknown, row: any) => (
                  <Popover
                    zIndex={4200}
                    trigger={['hover', 'click']}
                    placement="bottomLeft"
                    mouseEnterDelay={0.12}
                    getPopupContainer={() => document.body}
                    content={<CompetencyEvaluateePopoverContent row={row} />}
                  >
                    <span
                      style={{
                        cursor: 'pointer',
                        color: '#1677ff',
                        borderBottom: '1px dashed rgba(22,119,255,0.55)',
                      }}
                    >
                      {row.evaluatee?.name ?? '—'}
                    </span>
                  </Popover>
                ),
              },
              {
                title: '部门',
                dataIndex: ['evaluatee', 'deptPath'],
                width: 220,
                ellipsis: true,
                render: (v?: string) =>
                  v ? (
                    <Typography.Text title={v}>
                      {v.split('/').slice(-2).join(' / ')}
                    </Typography.Text>
                  ) : (
                    '-'
                  ),
              },
              { title: '职位', dataIndex: ['evaluatee', 'position'], width: 180, ellipsis: true },
              {
                title: '胜任力',
                dataIndex: 'status',
                width: 100,
                render: (v: string) => (
                  <Tag color={taskStatusColor(v)}>{taskStatusLabel(v)}</Tag>
                ),
              },
              {
                title: '高潜识别',
                width: 130,
                render: (_: any, r: any) => {
                  const info = hpStatusByEmpId.get(r.evaluateeId);
                  if (!info?.status) {
                    return <Tag color={taskStatusColor('PENDING')}>{taskStatusLabel('PENDING')}</Tag>;
                  }
                  if (info.status === 'SUBMITTED') {
                    return info.isHp ? (
                      <Tag color="gold" icon={<CrownOutlined />}>
                        已识别·高潜
                      </Tag>
                    ) : (
                      <Tag color="blue">已识别·非高潜</Tag>
                    );
                  }
                  return (
                    <Tag color={taskStatusColor(info.status)}>
                      {taskStatusLabel(info.status)}
                    </Tag>
                  );
                },
              },
              {
                title: '操作',
                width: 110,
                render: (_, r: any) => (
                  <Button size="small" type="primary" onClick={() => goEval(r.id)}>
                    {r.status === 'SUBMITTED' ? '查看 / 修改' : '去打分'}
                  </Button>
                ),
              },
            ]}
            onRow={(r: any) => ({
              onClick: () => goEval(r.id),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>
    </div>
  );
}
